/**
 * Search's filter panel: the shared `FilterTable` with search's rows.
 *
 * Scope and engine are exclusive rows; owner/tag/type are contextual facets
 * (OR within a row, AND across); previews/descriptions live in the display
 * section. The panel owns the semantics and the closed summary; the table owns
 * the presentation.
 */

import { useT, type Translate } from "../../shared/i18n";
import { FilterTable, type FilterRow } from "../../shared/components/FilterTable";
import { itemCategoryLabel } from "../../shared/components/CategoryChip";
import {
  describeFacets,
  toggleFacetValue,
  type FacetOptions,
  type SearchDisplayState,
  type SearchFacetState,
} from "./facets";
import { DEFAULT_SEARCH_MODE, type SearchMode, type SearchScope } from "./searchUrl";

type FacetKey = keyof SearchFacetState;
type DisplayKey = keyof SearchDisplayState;

const PROPERTY_ROWS: ReadonlyArray<{ facet: FacetKey }> = [
  { facet: "owners" },
  { facet: "tags" },
  { facet: "types" },
];

const DISPLAY_ROWS: ReadonlyArray<{ key: DisplayKey }> = [
  { key: "previews" },
  { key: "descriptions" },
];

const SCOPE_VALUES: ReadonlyArray<SearchScope> = ["all", "containers", "items", "photos"];

export function SearchFilterPanel({
  scope,
  onScopeChange,
  mode,
  onModeChange,
  facets,
  onFacets,
  display,
  onDisplay,
  options,
}: {
  scope: SearchScope;
  onScopeChange: (scope: SearchScope) => void;
  mode: SearchMode;
  onModeChange: (mode: SearchMode) => void;
  facets: SearchFacetState;
  onFacets: (next: SearchFacetState) => void;
  display: SearchDisplayState;
  onDisplay: (next: SearchDisplayState) => void;
  options: FacetOptions;
}) {
  const t = useT();
  const activeCount = facets.owners.length + facets.tags.length + facets.types.length;
  const hiddenDisplay = DISPLAY_ROWS.filter(({ key }) => !display[key]);

  const scopeLabel =
    scope === "all"
      ? ""
      : scope === "containers"
        ? t("scopeContainers")
        : scope === "items"
          ? t("scopeItems")
          : t("scopePhotos");

  const summary = [
    scopeLabel === "" ? "" : `${t("filterScope")}: ${scopeLabel}`,
    mode === DEFAULT_SEARCH_MODE
      ? ""
      : `${t("searchMode")}: ${t(mode === "semantic" ? "searchModeSemantic" : "searchModeKeyword")}`,
    activeCount > 0
      ? describeFacets(facets, (facet, value) =>
          facet === "types" ? itemCategoryLabel(value, t) : value,
        )
      : "",
    hiddenDisplay.length === 0
      ? ""
      : `${t("filterDisplay")}: ${hiddenDisplay.map(({ key }) => displayLabel(key, t)).join(", ")} ${t("filterHide")}`,
  ]
    .filter(Boolean)
    .join(" · ");

  const scopeRow: FilterRow = {
    key: "scope",
    label: t("filterScope"),
    values: SCOPE_VALUES.map((value) => ({
      value,
      label:
        value === "all"
          ? t("scopeAll")
          : value === "containers"
            ? t("scopeContainers")
            : value === "items"
              ? t("scopeItems")
              : t("scopePhotos"),
      selected: scope === value,
      testId: `scope-value-${value}`,
    })),
    onSelect: (value) => onScopeChange(value as SearchScope),
  };

  const engineRow: FilterRow = {
    key: "engine",
    label: t("searchMode"),
    values: (["keyword", "semantic"] as const).map((value) => ({
      value,
      label: t(value === "semantic" ? "searchModeSemantic" : "searchModeKeyword"),
      selected: mode === value,
      testId: `engine-value-${value}`,
    })),
    onSelect: (value) => onModeChange(value as SearchMode),
  };

  const facetRows: FilterRow[] = PROPERTY_ROWS.map(({ facet }) => ({
    key: facet,
    label:
      facet === "owners" ? t("filterOwner") : facet === "tags" ? t("filterTag") : t("filterType"),
    values: options[facet].map((option) => ({
      value: option.value,
      label: facetOptionLabel(facet, option.value, t),
      count: option.count,
      selected: facets[facet].includes(option.value),
      testId: `facet-value-${facet}-${option.value || "unassigned"}`,
    })),
    onSelect: (value) =>
      onFacets({
        ...facets,
        [facet]: toggleFacetValue(facets[facet], value),
      }),
  }));

  return (
    <FilterTable
      rows={[scopeRow, engineRow, ...facetRows]}
      displayRows={DISPLAY_ROWS.map(({ key }) => ({
        key,
        label: displayLabel(key, t),
        shown: display[key],
        onChange: (shown) => onDisplay({ ...display, [key]: shown }),
      }))}
      activeCount={activeCount}
      summary={summary}
      onClear={() => onFacets({ owners: [], tags: [], types: [] })}
    />
  );
}

function displayLabel(key: DisplayKey, t: Translate): string {
  return key === "previews" ? t("filterPreviews") : t("filterDescriptions");
}

function facetOptionLabel(
  facet: FacetKey,
  value: string,
  t: Translate,
): string {
  if (facet === "owners") return value === "" ? t("filterUnassigned") : value;
  if (facet === "types") return itemCategoryLabel(value, t);
  return value;
}
